import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'reactstrap';

import Button from 'components/App/Button';
import ColGroup from 'components/ColGroup';

import { convertIsoDateToString } from 'utils';

import styles from './DataTableVoucher.module.scss';

const HEADS = [
  'Tên voucher',
  'Loại voucher',
  'Ngày bắt đầu',
  'Ngày kết thúc',
  'Ngày hết hạn voucher',
  'Tổng số lượng',
  'Đã sử dụng',
];

const COL_WIDTHS = [2.5, 1, 1.6, 1.6, 1.6, 1, 1];
const COL_WIDTH_SUM = COL_WIDTHS.reduce((sum, width) => sum + width, 0);

const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';

const VOUCHER_TYPE_LABELS = {
  BOOKESE: 'BOOKESE',
  PARTNER: 'PARTNER',
};

const formatDateTime = (date) => {
  const formatted = convertIsoDateToString(date, DATE_TIME_FORMAT);
  return formatted || '-';
};

const formatVoucherType = (type) => VOUCHER_TYPE_LABELS[type] || type || '-';

const formatNumber = (num) => {
  if (num === undefined || num === null) return '-';
  return new Intl.NumberFormat('vi-VN').format(num);
};

function DataTableVoucher(props) {
  const { voucherList, onDeleteVoucher, onViewVoucherDetail } = props;

  const renderActions = useCallback(
    (voucher) => (
      <div className="Action d-flex gap-3 mt-1">
        <Button
          outline
          classes="Action__Button Action__Button__Danger"
          onClick={() => onDeleteVoucher?.(voucher)}
        >
          Xóa
        </Button>

        <Button
          outline
          classes="Action__Button Action__Button__Link"
          onClick={() => onViewVoucherDetail?.(voucher)}
        >
          Xem chi tiết
        </Button>
      </div>
    ),
    [onDeleteVoucher, onViewVoucherDetail],
  );

  return (
    <div
      className={styles.Component}
      style={{ '--voucher-table-cols': COL_WIDTH_SUM }}
    >
      {voucherList.length === 0 ? (
        <div className="NoData">Chưa có dữ liệu</div>
      ) : (
        <Table className="Table">
          <ColGroup
            list={COL_WIDTHS}
            containerWidth={COL_WIDTH_SUM}
            paddingX={0}
          />
          <thead className="Table__Header">
            <tr className="Table__Header__Row">
              {HEADS.map((head) => (
                <th key={head} className="Table__Header__Row__Content">
                  {head}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="Table__Body">
            {voucherList.map((voucher) => (
              <tr className="Table__Body__Row" key={voucher._id}>
                <td className="Table__Body__Row__Content">
                  <div className="VoucherName">
                    <div className="VoucherName__Info">
                      <span className="VoucherName__Text">
                        {voucher.name || '-'}
                      </span>
                      {/* {voucher.code && (
                        <span className="VoucherName__Code">
                          Mã:
                          {voucher.code}
                        </span>
                      )} */}
                      {renderActions(voucher)}
                    </div>
                  </div>
                </td>

                <td className="Table__Body__Row__Content">
                  <div className="VoucherType">
                    {formatVoucherType(voucher.useType)}
                  </div>
                </td>

                <td className="Table__Body__Row__Content">
                  <div className="DateCell">
                    {formatDateTime(voucher.startAt)}
                  </div>
                </td>

                <td className="Table__Body__Row__Content">
                  <div className="DateCell">
                    {formatDateTime(voucher.endAt)}
                  </div>
                </td>

                <td className="Table__Body__Row__Content">
                  <div className="DateCell">
                    {formatDateTime(voucher.voucherExpiryAt)}
                  </div>
                </td>

                <td className="Table__Body__Row__Content">
                  <div className="NumberCell">
                    {formatNumber(voucher.totalVoucher)}
                  </div>
                </td>

                <td className="Table__Body__Row__Content">
                  <div className="NumberCell">
                    {formatNumber(voucher.usedVoucher)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

DataTableVoucher.propTypes = {
  voucherList: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      // code: PropTypes.string,
      name: PropTypes.string,
      useType: PropTypes.string,
      startAt: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
      endAt: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
      voucherExpiryAt: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
      totalVoucher: PropTypes.number,
      usedVoucher: PropTypes.number,
    }),
  ),
  onDeleteVoucher: PropTypes.func,
  onViewVoucherDetail: PropTypes.func,
};

DataTableVoucher.defaultProps = {
  voucherList: [],
  onDeleteVoucher: undefined,
  onViewVoucherDetail: undefined,
};

export default memo(DataTableVoucher);
